import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CARET_LARGE } from '@/components/switchback/Icon/assets';
import Icon from '@/components/switchback/Icon/IconComponent';
import Link from '@/components/switchback/Link/Link';
import { breakpoint } from '@/constants/breakpoint';
import useWindowSize from '@/hooks/useWindowSize';
import { generateSimpleId } from '@/utility/helpers';

import css from './CaptionBox.module.css';

type ICaptionBoxElement = React.HTMLAttributes<HTMLDivElement>;

interface IProps {
  header: React.ReactNode;
  linkText: string;
  linkTextCollapsed: string;
  className?: string;
}

const CaptionBox: React.FC<IProps & ICaptionBoxElement> = ({
  header,
  children,
  linkText,
  linkTextCollapsed,
  className,
  ...props
}) => {
  const [isCollapsed, setIscollapsed] = useState(false);
  const buttonId = useMemo(() => `button_${generateSimpleId()}`, []);
  const panelId = useMemo(() => `panel_${generateSimpleId()}`, []);
  const windowSize = useWindowSize();

  useEffect(() => {
    windowSize?.width && setIscollapsed(windowSize.width > breakpoint.lg);
  }, [windowSize]);

  const toggleContentDisplay = useCallback(() => {
    setIscollapsed(!isCollapsed);
  }, [isCollapsed]);

  return (
    <div
      data-testid="caption-box"
      className={`${className} ${css.caption} bg-white rounded-md overflow-hidden md:w-full`}
      {...props}>
      <div
        className={`${css.header} autoType300 px-4 py-5 flex items-center justify-between md:px-8 lg:px-5`}>
        {header && <span className="pr-4 font-bold">{header}</span>}
        {!header && !isCollapsed && (
          <p className="block w-full pr-4 text-gray-500 truncate">{children}</p>
        )}
        <Link
          onClick={toggleContentDisplay}
          aria-expanded={isCollapsed}
          aria-controls={panelId}
          id={buttonId}
          className={`${css.link} ml-auto whitespace-nowrap`}>
          {isCollapsed ? linkTextCollapsed : linkText}
          <Icon
            width={11}
            className={`${css.arrow} transition-all duration-300`}
            name={CARET_LARGE}
          />
        </Link>
      </div>

      <div
        id={panelId}
        data-testid="caption-box-panel"
        aria-labelledby={buttonId}
        data-collapsed={String(isCollapsed)}
        className={`${css.panel} pb-6 px-4 md:px-8 lg:px-5 autoType500 text-gray-500 overflow-hidden transition-all ease-in-out duration-300`}>
        <div className={`${css.scroll}`} tabIndex={0}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CaptionBox;
